import React, { useState, useEffect } from 'react';
import api from '../api/api';
import './KYCAnalytics.css';

const KYCAnalytics = ({ merchantId }) => {
  const [analytics, setAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    generateAnalytics();
  }, [merchantId, timeRange]);

  const generateAnalytics = async () => {
    try {
      // Fetch analytics from API
      const response = await api.get(`/api/kyc/analytics/${merchantId}`, {
        params: { timeRange }
      });
      
      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Fallback to empty analytics
      setAnalytics({
        totalDocuments: 0,
        verifiedDocuments: 0,
        rejectedDocuments: 0,
        pendingDocuments: 0,
        averageScore: 0,
        documentTypes: {},
        weeklyTrend: [],
        verificationTimes: { average: 0, fastest: 0, slowest: 0 }
      });
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getSuccessRate = () => {
    if (!analytics) return 0;
    return Math.round((analytics.verifiedDocuments / analytics.totalDocuments) * 100);
  };

  if (!analytics) {
    return (
      <div className="analytics-loading">
        <div className="loading-spinner"></div>
        <p>Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="kyc-analytics">
      <div className="analytics-header">
        <h3>📊 KYC Analytics Dashboard</h3>
        <div className="time-range-selector">
          <select 
            value={timeRange} 
            onChange={(e) => setTimeRange(e.target.value)}
            className="time-range-select"
          >
            <option value="1d">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-icon">📄</div>
          <div className="metric-info">
            <h4>{analytics.totalDocuments}</h4>
            <p>Total Documents</p>
          </div>
        </div>

        <div className="metric-card success">
          <div className="metric-icon">✅</div>
          <div className="metric-info">
            <h4>{analytics.verifiedDocuments}</h4>
            <p>Verified</p>
          </div>
        </div>

        <div className="metric-card danger">
          <div className="metric-icon">❌</div>
          <div className="metric-info">
            <h4>{analytics.rejectedDocuments}</h4>
            <p>Rejected</p>
          </div>
        </div>

        <div className="metric-card warning">
          <div className="metric-icon">⏳</div>
          <div className="metric-info">
            <h4>{analytics.pendingDocuments}</h4>
            <p>Pending</p>
          </div>
        </div>
      </div>

      {/* Success Rate and Average Score */}
      <div className="performance-metrics">
        <div className="performance-card">
          <h4>Success Rate</h4>
          <div className="circular-progress">
            <div 
              className="progress-circle"
              style={{ '--progress': getSuccessRate() }}
            >
              <span>{getSuccessRate()}%</span>
            </div>
          </div>
        </div>

        <div className="performance-card">
          <h4>Average Score</h4>
          <div className="score-display">
            <span className="score-number">{analytics.averageScore}</span>
            <span className="score-label">out of 100</span>
          </div>
        </div>
      </div>

      {/* Document Types Distribution */}
      <div className="chart-section">
        <h4>Document Types Distribution</h4>
        <div className="document-types-chart">
          {Object.entries(analytics.documentTypes).map(([type, count]) => {
            const percentage = (count / analytics.totalDocuments) * 100;
            return (
              <div key={type} className="document-type-bar">
                <div className="bar-info">
                  <span className="bar-label">{type.replace('_', ' ')}</span>
                  <span className="bar-value">{count}</span>
                </div>
                <div className="bar-container">
                  <div 
                    className="bar-fill"
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Weekly Trend */}
      <div className="chart-section">
        <h4>Weekly Upload Trend</h4>
        <div className="trend-chart">
          {analytics.weeklyTrend.map((value, index) => {
            const maxValue = Math.max(...analytics.weeklyTrend);
            const height = (value / maxValue) * 100;
            const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
            
            return (
              <div key={index} className="trend-bar">
                <div 
                  className="trend-fill"
                  style={{ height: `${height}%` }}
                  title={`${days[index]}: ${value} uploads`}
                ></div>
                <span className="trend-label">{days[index]}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Verification Times */}
      <div className="verification-times">
        <h4>Verification Performance</h4>
        <div className="times-grid">
          <div className="time-metric">
            <span className="time-label">Average Time</span>
            <span className="time-value">{formatTime(analytics.verificationTimes.average)}</span>
          </div>
          <div className="time-metric">
            <span className="time-label">Fastest</span>
            <span className="time-value success">{formatTime(analytics.verificationTimes.fastest)}</span>
          </div>
          <div className="time-metric">
            <span className="time-label">Slowest</span>
            <span className="time-value danger">{formatTime(analytics.verificationTimes.slowest)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KYCAnalytics;